// Client JS-error capture → owned analytics beacon → events_raw (rule 34: this
// is telemetry, so it rides the SAME edge beacon as every other browser event —
// NEVER a Cloud Run /api route). This is the "Sentry-in-BigQuery" frontend layer:
// it makes window.onerror / onunhandledrejection / React-error-boundary failures
// queryable in events_raw and joinable with the rest of the funnel, independent of
// Sentry's quota-limited free tier (Sentry stays as the bonus UI).
//
// Hard requirements baked in here so every caller (global handlers + the React
// ErrorBoundary) shares ONE shaping + throttle implementation (no drifting copies,
// rules 23/24):
//   • ≤ MAX_ERRORS_PER_SESSION beacons per session, and identical message+stack
//     de-duped within a session, so an error LOOP can never flood events_raw.
//   • The handler itself NEVER throws (everything is wrapped) — an error in the
//     error reporter must not cascade.
//   • The raw stack is kept (first ~2KB) but SPLIT into ≤256-char chunks because
//     the beacon sink truncates every individual param value at 256 chars
//     (beacon-bigquery.ts / beacon-edge.ts sanitizeValue). Chunking preserves a
//     triageable stack (message + component + page + first frames) WITHOUT a
//     blanket per-value cap bump that would bloat every other event.

import { sendOwnedAnalyticsEvent } from "@/lib/analytics-server";
import { isBenignBrowserNoise } from "@/lib/js-error-noise";
import { scrubPii } from "@/lib/pii-scrub";

const MAX_ERRORS_PER_SESSION = 5;
const STACK_MAX_CHARS = 2048;
const PARAM_VALUE_MAX = 256; // mirror of the beacon sink's per-value cap
const MESSAGE_MAX_CHARS = 500;

// Per-page-load (per-isolate) state. sessionStorage is unreliable for a count
// that must survive a full crash loop within one page, and these handlers live
// for the lifetime of the document, so a module-level guard is the right scope:
// it caps the flood for THIS document, which is exactly where a render loop runs.
let sentCount = 0;
const seenFingerprints = new Set<string>();

/** Test-only: reset the per-document throttle/dedupe state between cases. */
export function resetJsErrorBeaconForTests(): void {
  sentCount = 0;
  seenFingerprints.clear();
}

function deviceType(): string {
  if (typeof window === "undefined") return "unknown";
  return window.innerWidth < 768 ? "mobile" : "desktop";
}

function release(): string | undefined {
  // Same build identifier Sentry uses (build-time inlined). Lets a minified
  // stack be mapped to a release the way an error monitor would.
  return process.env.NEXT_PUBLIC_SENTRY_RELEASE || undefined;
}

// Split the (truncated) stack across error_stack_0..error_stack_N so each chunk
// survives the sink's 256-char per-value cap. The full first ~2KB is preserved.
function stackChunks(stack: string): Record<string, string> {
  const out: Record<string, string> = {};
  const trimmed = stack.slice(0, STACK_MAX_CHARS);
  for (let i = 0, part = 0; i < trimmed.length; i += PARAM_VALUE_MAX, part += 1) {
    out[`error_stack_${part}`] = trimmed.slice(i, i + PARAM_VALUE_MAX);
  }
  return out;
}

interface JsErrorInput {
  error_message: string;
  error_name?: string;
  error_stack?: string;
  /** Where a React ErrorBoundary caught it (component subtree label). */
  boundary?: string;
  /** React component stack, when available (from componentDidCatch). */
  component?: string;
}

// Build the js_error beacon params from a (message, stack) already normalized by
// the caller. Extracted to keep reportJsError under the complexity cap.
function jsErrorParams(
  input: JsErrorInput,
  message: string,
  stack: string
): Record<string, unknown> {
  const rel = release();
  return {
    error_message: message,
    error_name: (input.error_name ?? "Error").slice(0, PARAM_VALUE_MAX),
    page_location: scrubPii(window.location.href).slice(0, PARAM_VALUE_MAX),
    device_type: deviceType(),
    ...(rel ? { release: rel } : {}),
    ...(input.boundary ? { boundary: input.boundary.slice(0, PARAM_VALUE_MAX) } : {}),
    ...(input.component ? { component: input.component.slice(0, PARAM_VALUE_MAX) } : {}),
    ...stackChunks(stack),
  };
}

/**
 * Emit a single `js_error` beacon, throttled + de-duped per document. NEVER
 * throws. Returns true if a beacon was queued, false if it was throttled,
 * de-duped, or suppressed by an internal error.
 */
export function reportJsError(input: JsErrorInput): boolean {
  try {
    if (typeof window === "undefined") return false;
    if (sentCount >= MAX_ERRORS_PER_SESSION) return false;

    // PII-scrub message + stack before anything is stored or even fingerprinted
    // (emails/phones/tokens/PANs → redacted), so a thrown value embedding a
    // seeker's contact never reaches events_raw (rule 34 + Sentry-style scrub).
    const message = scrubPii(
      String(input.error_message || "Unknown error").slice(0, MESSAGE_MAX_CHARS)
    );
    const stack = scrubPii(input.error_stack ? String(input.error_stack) : "");
    // Drop benign third-party / unactionable browser noise (stackless GTM
    // SecurityError, restricted-context storage, cross-origin "Script error.")
    // so events_raw stays a signal of REAL app failures — the SAME drop list
    // Sentry uses (js-error-noise.ts, shared so the two sinks can't drift).
    if (isBenignBrowserNoise({ name: input.error_name, message, stack })) return false;
    // Dedupe identical message+stack within the document so a tight loop that
    // throws the SAME error can't spend the whole per-session budget on copies.
    const fingerprint = `${input.error_name ?? ""}|${message}|${stack.slice(0, STACK_MAX_CHARS)}`;
    if (seenFingerprints.has(fingerprint)) return false;
    seenFingerprints.add(fingerprint);
    sentCount += 1;

    sendOwnedAnalyticsEvent("js_error", jsErrorParams(input, message, stack));
    return true;
  } catch {
    // The error reporter must never throw (would cascade an onerror loop).
    return false;
  }
}

/**
 * Normalize an arbitrary thrown value (window.onerror's `error`, a rejection
 * reason, or an Error from componentDidCatch) into the js_error fields. NEVER
 * throws.
 */
export function jsErrorFieldsFromUnknown(value: unknown): {
  error_message: string;
  error_name: string;
  error_stack: string;
} {
  try {
    if (value instanceof Error) {
      return {
        error_message: value.message || value.name || "Error",
        error_name: value.name || "Error",
        error_stack: value.stack || "",
      };
    }
    if (typeof value === "string") {
      return { error_message: value, error_name: "Error", error_stack: "" };
    }
    return {
      error_message: String(value ?? "Unknown error"),
      error_name: "Error",
      error_stack: "",
    };
  } catch {
    return { error_message: "Unknown error", error_name: "Error", error_stack: "" };
  }
}
